// Área Tasks: kanban, My Tasks, Automations, Triggers e Archived.
// Task agora é issue do Linear (coluna = status do time, via MCP do LiteLLM);
// automação = workflow com `on: schedule`, trigger = workflow com
// `on: repository_dispatch` — esses dois seguem no GitHub Actions.
// Contratos em docs/screens/01..03-tasks-*.md.

import {
  empty, errorBox, esc, head, pill, since, skeleton, table, tabs, toast,
} from "../ui";
import { repoAtual,
  gateway, linear, litellm, SourceError,
  type Agent, type GhIssue, type LinearIssue, type LinearStatus,
} from "../api";

// ------------------------------------------------------------------ estilo

const CSS = `
/* Quantas colunas houver: quem manda é a lista de status do Linear. */
.tasks-board { display: grid; grid-auto-flow: column; grid-auto-columns: minmax(185px, 1fr); gap: 12px; overflow-x: auto; padding-bottom: 8px; }
@media (max-width: 1100px) { .tasks-board { grid-auto-columns: 210px; } }
.tasks-over { border-color: var(--accent); }
.tasks-card { cursor: grab; }
.tasks-right { display: flex; justify-content: flex-end; margin-bottom: 12px; }
.tasks-bloco { margin-bottom: 14px; }
.tasks-voltar { margin-bottom: 14px; display: inline-block; }
.tasks-yaml { min-height: 220px; }
.tasks-chev { background: none; border: none; color: var(--ink-3); cursor: pointer; font-size: 14px; }
.tasks-panel td { background: var(--surface); padding: 12px 16px; }
.tasks-sessions { display: grid; gap: 6px; font-family: var(--mono); font-size: 12px; }
.tasks-all { justify-self: start; background: none; border: none; padding: 0; color: var(--accent); cursor: pointer; font-family: inherit; font-size: inherit; }
.tasks-toggle { width: 34px; height: 18px; border-radius: 9px; border: 1px solid var(--rule); background: var(--surface-2); cursor: pointer; position: relative; }
.tasks-toggle::after { content: ""; position: absolute; top: 2px; left: 2px; width: 12px; height: 12px; border-radius: 50%; background: var(--ink-3); transition: left .15s; }
.tasks-toggle[data-state="active"] { background: var(--accent); border-color: var(--accent); }
.tasks-toggle[data-state="active"]::after { left: 18px; background: #12130E; }
.tasks-name { font-weight: 600; }
.tasks-name-sub { font-size: 11.5px; color: var(--ink-3); }
.tasks-kebab { position: relative; }
.tasks-kebab summary { cursor: pointer; list-style: none; padding: 2px 8px; color: var(--ink-3); }
.tasks-menu { position: absolute; right: 0; top: 100%; z-index: 5; background: var(--surface); border: 1px solid var(--rule); border-radius: 6px; display: grid; min-width: 130px; }
.tasks-menu button { background: none; border: none; color: var(--ink); text-align: left; padding: 8px 12px; cursor: pointer; font-size: 12.5px; }
.tasks-menu button:hover { background: var(--surface-2); }
.tasks-fire { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding: 7px 0; border-bottom: 1px solid var(--rule-2); font-size: 13px; }
.tasks-fire:last-child { border-bottom: none; }
`;

function injetaCss(): void {
  if (!document.getElementById("css-tasks")) {
    const st = document.createElement("style");
    st.id = "css-tasks";
    st.textContent = CSS;
    document.head.append(st);
  }
}

// ------------------------------------------------------------ infraestrutura


/** Esqueleto → corpo → erro tratado, o padrão das páginas atuais. */
async function load(
  el: HTMLElement,
  header: string,
  body: () => Promise<string>,
  wire?: () => void,
): Promise<void> {
  el.innerHTML = header + skeleton();
  try {
    el.innerHTML = header + (await body());
    wire?.();
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML = header + errorBox(err.source ?? "origem", err.message, err.next ?? "tente de novo");
  }
}

/** UTF-8 ↔ base64 — a API de contents fala base64 e btoa cru quebra em acento. */
function b64e(texto: string): string {
  let bin = "";
  new TextEncoder().encode(texto).forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin);
}
function b64d(conteudo: string): string {
  const bin = atob(conteudo.replace(/\n/g, ""));
  return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
}
// ---------------------------------------------------------------- workflows
//
// Uma listagem serve as três leituras: Automations (cron), Triggers
// (repository_dispatch) e a linha "Once"/cron dos cards do kanban.

interface Wf {
  id: number;
  name: string;
  path: string;
  state: string;
}
interface WfComYaml extends Wf {
  yaml: string;
  sha: string;
  cron: string | null;
}
interface Run {
  status: string;
  conclusion: string | null;
  created_at: string;
}

async function workflows(repo: string): Promise<WfComYaml[]> {
  const { workflows: ws } = await gateway<{ workflows: Wf[] }>(
    `github/repos/${repo}/actions/workflows?per_page=100`,
  );
  return Promise.all(
    ws.map(async (w) => {
      try {
        const f = await gateway<{ content: string; sha: string }>(
          `github/repos/${repo}/contents/${w.path}`,
        );
        const yaml = b64d(f.content);
        const cron = yaml.match(/cron:\s*["']?([^"'\n]+)/)?.[1]?.trim() ?? null;
        return { ...w, yaml, sha: f.sha, cron };
      } catch {
        return { ...w, yaml: "", sha: "", cron: null };
      }
    }),
  );
}

/** Cron humanizado, como o app de referência ("At 01:00 AM, only on Monday"). */
function humanCron(cron: string): string {
  const p = cron.trim().split(/\s+/);
  if (p.length !== 5) return cron;
  const [min, h, dom, , dow] = p;
  const dias = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  if (min === "*" && h === "*") return "Every minute";
  if (min.startsWith("*/") && h === "*") return `Every ${min.slice(2)} minutes`;
  if (/^\d+$/.test(min) && /^\d+$/.test(h)) {
    const hh = Number(h);
    const hora = `${String(hh % 12 === 0 ? 12 : hh % 12).padStart(2, "0")}:${min.padStart(2, "0")} ${hh < 12 ? "AM" : "PM"}`;
    if (/^\d+$/.test(dow) && dom === "*") return `At ${hora}, only on ${dias[Number(dow) % 7]}`;
    if (dom === "*" && dow === "*") return `At ${hora}, every day`;
    return `At ${hora}`;
  }
  return cron;
}

/** Convenções lidas do YAML: comentário da 1ª linha é a descrição; `agent:`
 *  nomeia quem dispara; menção a ISSUE_TEMPLATE define modo e alvo. */
const descDoYaml = (yaml: string): string | null => yaml.match(/^#\s*(.+)/)?.[1]?.trim() ?? null;
const agenteDoYaml = (yaml: string): string | null =>
  yaml.match(/(?:^|\n)\s*(?:#\s*)?agent:\s*["']?([^"'\n]+)/)?.[1]?.trim() ?? null;
const alvoDoYaml = (yaml: string): string | null =>
  yaml.match(/ISSUE_TEMPLATE\/([\w.-]+)/)?.[1]?.replace(/\.(md|ya?ml)$/, "") ?? null;

function nomeAgente(agents: Agent[], chave: string | null): string | null {
  if (!chave) return null;
  const norm = (s: string) => s.toLowerCase().replace(/[\s_]+/g, "-");
  const a = agents.find(
    (x) => norm(x.agent_name) === norm(chave) || norm(x.agent_card_params?.name ?? "") === norm(chave),
  );
  return a?.agent_card_params?.name ?? a?.agent_name ?? chave;
}

// ------------------------------------------------------------------- casca

type Aba = "my" | "tasks" | "automations" | "triggers" | "archived";

function cabecalho(aba: Aba): string {
  return (
    head({
      title: "Tasks",
      actions: `<a class="btn btn-primary" href="#/tasks/new">+ Create Task</a>`,
    }) +
    tabs([
      { label: "My Tasks", href: "#/tasks/my", ativa: aba === "my" },
      { label: "Tasks", href: "#/tasks", ativa: aba === "tasks" },
      { label: "Automations", href: "#/tasks/automations", ativa: aba === "automations" },
      { label: "Triggers", href: "#/tasks/triggers", ativa: aba === "triggers" },
      { label: "Archived", href: "#/tasks/archived", ativa: aba === "archived" },
    ])
  );
}

export async function tasks(el: HTMLElement): Promise<void> {
  injetaCss();
  const seg = location.hash.replace(/^#\/?/, "").split("/");
  const sub = seg[1] ?? "";
  if (sub === "triggers" && seg[2]) return detalheTrigger(el, seg[2]);

  const aba: Aba =
    (["my", "automations", "triggers", "archived"] as const).find((a) => a === sub) ?? "tasks";
  const header = cabecalho(aba);

  if (aba === "automations") return paginaAutomations(el, header);
  if (aba === "triggers") return paginaTriggers(el, header);
  if (aba === "archived") return paginaArchived(el, header);
  if (aba === "my") {
    // Filtrar por assignee exige saber quem é você no Linear, e o cliente ainda
    // não expõe isso. Board cheio aqui seria mentira — melhor dizer o que falta.
    el.innerHTML = header + empty(
      "My Tasks ainda não sabe quem é você",
      "O MCP do Linear devolve o assignee como nome; falta um linear.me() em api.ts para filtrar o board por ele.",
    );
    return;
  }
  return paginaBoard(el, header);
}

// ------------------------------------------------------------------ kanban

// As colunas são os status do time no Linear, lidos de `list_issue_statuses` —
// nada de lista escrita à mão. O `type` do status já é a ordem do fluxo
// (backlog → unstarted → started → completed); `triage` e `canceled` ficam fora
// do board (canceled é a aba Archived). Dois status "started" (In Progress e
// In Review) mantêm a ordem em que a fonte devolveu — sort é estável.
const ORDEM: Record<string, number> = { backlog: 0, unstarted: 1, started: 2, completed: 3 };

const colunasDe = (st: LinearStatus[]): LinearStatus[] =>
  st.filter((s) => s.type in ORDEM).sort((a, b) => ORDEM[a.type] - ORDEM[b.type]);

async function paginaBoard(el: HTMLElement, header: string): Promise<void> {
  let ctx: { issues: LinearIssue[] } | null = null;

  await load(
    el,
    header,
    async () => {
      // Sem catch: se o servidor `linear_oficial` não estiver autorizado no
      // LiteLLM, o erro da origem sobe e vira errorBox — board vazio seria mentira.
      const [statuses, issues] = await Promise.all([linear.statuses(), linear.issues()]);
      ctx = { issues };

      return `<div class="tasks-board">${colunasDe(statuses)
        .map((c) => {
          const dela = issues.filter((i) => i.status === c.name);
          return `
          <section class="kcol">
            <div class="kcol-head"><span>${esc(c.name)}</span><span class="kcol-count">${dela.length}</span></div>
            <div class="kcol-body" data-col="${esc(c.name)}">
              ${dela.map((i) => cartao(i)).join("") || '<div class="kcol-empty">Drop tasks here</div>'}
            </div>
          </section>`;
        })
        .join("")}</div>`;
    },
    () => {
      if (ctx) wireBoard(el, ctx.issues);
    },
  );
}

function cartao(i: LinearIssue): string {
  // Prioridade "None" (valor 0) não vira pill — é ausência, não informação.
  const prio = i.priority && i.priority.value > 0 && i.priority.name !== "None" ? i.priority : null;
  return `
    <div class="kcard tasks-card" draggable="true" data-id="${esc(i.id)}">
      <div class="kcard-meta mono">${esc(i.id)}</div>
      <div class="kcard-title">${esc(i.title)}</div>
      ${i.project ? `<div class="kcard-meta">${esc(i.project)}</div>` : ""}
      <div class="kcard-foot">
        <span class="kcard-meta">${esc(i.assignee ?? "Unassigned")}</span>
        ${prio ? pill(prio.name, prio.name === "Urgent" ? "crit" : "idle") : ""}
      </div>
    </div>`;
}

function wireBoard(el: HTMLElement, issues: LinearIssue[]): void {
  el.querySelectorAll<HTMLElement>(".tasks-card").forEach((c) => {
    c.addEventListener("dragstart", (ev) => ev.dataTransfer?.setData("text/plain", c.dataset.id ?? ""));
  });

  el.querySelectorAll<HTMLElement>("[data-col]").forEach((col) => {
    col.addEventListener("dragover", (ev) => {
      ev.preventDefault();
      col.classList.add("tasks-over");
    });
    col.addEventListener("dragleave", () => col.classList.remove("tasks-over"));
    col.addEventListener("drop", async (ev) => {
      ev.preventDefault();
      col.classList.remove("tasks-over");
      const id = ev.dataTransfer?.getData("text/plain") ?? "";
      const destino = col.dataset.col;
      const issue = issues.find((i) => i.id === id);
      if (!issue || !destino || issue.status === destino) return;
      try {
        // Mudar de coluna é mudar o status da issue no Linear — só isso.
        await linear.salvaIssue({ id, status: destino });
        toast(`${id} → ${destino}`);
        void tasks(el); // relê a fonte e repinta: sem F5
      } catch (e) {
        toast((e as SourceError).message);
      }
    });
  });
}

// -------------------------------------------------------------- automations

async function paginaAutomations(el: HTMLElement, header: string): Promise<void> {
  let ctx: { repo: string } | null = null;

  await load(
    el,
    header,
    async () => {
      const repo = await repoAtual();
      const [ws, agents] = await Promise.all([
        workflows(repo),
        litellm.agents().catch(() => [] as Agent[]),
      ]);
      const autos = ws.filter((w) => w.cron);
      ctx = { repo };

      if (!autos.length) {
        return empty(
          "Nenhuma automação",
          'Automação é workflow com "on: schedule" no repo. Crie uma task com recorrência e ela aparece aqui.',
        );
      }

      const linhas = autos.flatMap((w) => [
        `<tr>
          <td><button class="tasks-chev" data-exp="${w.id}" type="button" aria-label="expandir">›</button></td>
          <td>${esc(w.name)}</td>
          <td>${esc(nomeAgente(agents, agenteDoYaml(w.yaml)) ?? "—")}</td>
          <td>${esc(humanCron(w.cron!))}</td>
          <td><button class="tasks-toggle" data-tog="${w.id}" data-state="${esc(w.state)}" type="button" aria-label="ligar ou desligar"></button></td>
        </tr>`,
        `<tr class="tasks-panel" data-panel="${w.id}" hidden><td colspan="5">Carregando…</td></tr>`,
      ]);
      return table(["", "Title", "Agent", "Schedule", "Status"], linhas);
    },
    () => {
      if (ctx) wireAutomations(el, ctx.repo);
    },
  );
}

function wireAutomations(el: HTMLElement, repo: string): void {
  // "View all sessions →" é a mesma rota do painel sem `per_page` (contrato 02):
  // troca as 3 últimas pela listagem completa, sem sair do app.
  async function pintaSessoes(painel: HTMLTableRowElement, id: string, todas: boolean): Promise<void> {
    try {
      const { workflow_runs } = await gateway<{ workflow_runs: Run[] }>(
        `github/repos/${repo}/actions/workflows/${id}/runs${todas ? "" : "?per_page=3"}`,
      );
      const sessoes = workflow_runs
        .map((r) => {
          const rodando = r.status === "in_progress" || r.status === "queued";
          const estado = rodando ? "Running" : (r.conclusion ?? r.status);
          // Data no formato do pixel: "Aug 12, 2:16 PM".
          const quando = new Date(r.created_at).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          });
          return `<div>${esc(quando)}${rodando ? " · Running..." : ""} · <strong>${esc(estado)}</strong></div>`;
        })
        .join("");
      painel.firstElementChild!.innerHTML = `
        <div class="tasks-sessions">
          <div>Recent sessions:</div>
          ${sessoes || "<div>Nenhuma sessão ainda.</div>"}
          ${todas ? "" : `<button class="tasks-all" type="button">View all sessions →</button>`}
        </div>`;
      painel
        .querySelector<HTMLButtonElement>(".tasks-all")
        ?.addEventListener("click", () => void pintaSessoes(painel, id, true));
      painel.dataset.ok = "1";
    } catch (e) {
      painel.firstElementChild!.textContent = (e as SourceError).message;
    }
  }

  el.querySelectorAll<HTMLButtonElement>("[data-exp]").forEach((b) =>
    b.addEventListener("click", async () => {
      const id = b.dataset.exp!;
      const painel = el.querySelector<HTMLTableRowElement>(`[data-panel="${id}"]`)!;
      painel.hidden = !painel.hidden;
      b.textContent = painel.hidden ? "›" : "⌄";
      if (painel.hidden || painel.dataset.ok) return;
      await pintaSessoes(painel, id, false);
    }),
  );

  el.querySelectorAll<HTMLButtonElement>("[data-tog]").forEach((b) =>
    b.addEventListener("click", async () => {
      const ligada = b.dataset.state === "active";
      b.disabled = true;
      try {
        await gateway(
          `github/repos/${repo}/actions/workflows/${b.dataset.tog}/${ligada ? "disable" : "enable"}`,
          { method: "PUT" },
        );
        toast(ligada ? "Automação desligada" : "Automação ligada");
        void tasks(el);
      } catch (e) {
        toast((e as SourceError).message);
        b.disabled = false;
      }
    }),
  );
}

// ----------------------------------------------------------------- triggers

const yamlTrigger = (nome: string, slug: string, desc: string): string =>
  [
    `# ${desc || nome}`,
    `name: ${JSON.stringify(nome)}`,
    "on:",
    "  repository_dispatch:",
    `    types: [${JSON.stringify(slug)}]`,
    "jobs:",
    "  fire:",
    "    runs-on: ubuntu-latest",
    "    steps:",
    `      - run: echo ${JSON.stringify(slug)}`,
    "",
  ].join("\n");
// ponytail: mesmo teto do yamlAgenda — o commit É a criação do trigger; o
// emissor externo dispara com POST /repos/{r}/dispatches (event_type = slug).

interface Trig extends WfComYaml {
  total: number;
  ultimo: string | null;
}

async function paginaTriggers(el: HTMLElement, header: string): Promise<void> {
  let ctx: { repo: string } | null = null;

  await load(
    el,
    header,
    async () => {
      const repo = await repoAtual();
      const ws = await workflows(repo);
      const evento = ws.filter((w) => !w.cron && w.yaml.includes("repository_dispatch"));
      const trigs: Trig[] = await Promise.all(
        evento.map(async (w) => {
          const r = await gateway<{ total_count: number; workflow_runs: Run[] }>(
            `github/repos/${repo}/actions/workflows/${w.id}/runs?per_page=1`,
          ).catch(() => ({ total_count: 0, workflow_runs: [] as Run[] }));
          return { ...w, total: r.total_count, ultimo: r.workflow_runs[0]?.created_at ?? null };
        }),
      );
      ctx = { repo };

      const novo = `
        <div class="tasks-right"><button class="btn btn-primary" id="tasks-novo-trigger" type="button">+ New Trigger</button></div>
        <form class="card tasks-bloco" id="tasks-form-trigger" hidden><div class="card-body">
          <div class="field">
            <label class="label" for="tgn">Nome</label>
            <input class="input" id="tgn" name="nome" required autocomplete="off" />
          </div>
          <div class="field">
            <label class="label" for="tgd">Descrição</label>
            <input class="input" id="tgd" name="desc" autocomplete="off" />
            <span class="hint">Vira o comentário da 1ª linha do workflow — o subtítulo na tabela.</span>
          </div>
          <button class="btn btn-primary" type="submit">Criar trigger</button>
        </div></form>`;

      if (!trigs.length) {
        return (
          novo +
          empty(
            "Nenhum trigger",
            'Trigger é workflow com "on: repository_dispatch". O sistema externo dispara com um webhook e cada disparo vira uma task.',
          )
        );
      }

      const linhas = trigs.map((w) => {
        const desc = descDoYaml(w.yaml);
        return `
        <tr>
          <td>
            <a class="tasks-name" href="#/tasks/triggers/${w.id}">${esc(w.name)}</a>
            ${desc ? `<div class="tasks-name-sub">${esc(desc)}</div>` : ""}
          </td>
          <td>${w.yaml.includes("ISSUE_TEMPLATE") ? pill("Template", "agent") : "—"}</td>
          <td>${esc(alvoDoYaml(w.yaml) ?? "—")}</td>
          <td>${w.state === "active" ? pill("Enabled", "ok") : pill("Disabled", "idle")}</td>
          <td>${w.ultimo ? esc(since(w.ultimo)) : "—"}</td>
          <td class="mono">${w.total}</td>
          <td>
            <details class="tasks-kebab"><summary aria-label="ações do trigger">⋮</summary>
              <div class="tasks-menu">
                <button type="button" data-tog="${w.id}" data-state="${esc(w.state)}">${w.state === "active" ? "Disable" : "Enable"}</button>
                <button type="button" data-del="${w.id}" data-path="${esc(w.path)}" data-sha="${esc(w.sha)}">Excluir</button>
              </div>
            </details>
          </td>
        </tr>`;
      });
      return novo + table(["Name", "Mode", "Target", "Status", "Last Fired", "Fires", ""], linhas);
    },
    () => {
      if (ctx) wireTriggers(el, ctx.repo);
    },
  );
}

function wireTriggers(el: HTMLElement, repo: string): void {
  const abrir = el.querySelector<HTMLButtonElement>("#tasks-novo-trigger");
  const form = el.querySelector<HTMLFormElement>("#tasks-form-trigger");
  abrir?.addEventListener("click", () => {
    if (form) form.hidden = !form.hidden;
  });

  form?.addEventListener("submit", async (e) => {
    e.preventDefault();
    const d = new FormData(form);
    const nome = String(d.get("nome") ?? "").trim();
    if (!nome) return;
    const slug = nome.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const enviar = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    enviar.disabled = true;
    try {
      await gateway(`github/repos/${repo}/contents/.github/workflows/${slug}.yml`, {
        method: "PUT",
        body: JSON.stringify({
          message: `trigger: ${nome}`,
          content: b64e(yamlTrigger(nome, slug, String(d.get("desc") ?? "").trim())),
        }),
      });
      toast(`Trigger "${nome}" criado`);
      void tasks(el);
    } catch (err) {
      toast((err as SourceError).message);
      enviar.disabled = false;
    }
  });

  el.querySelectorAll<HTMLButtonElement>(".tasks-menu [data-tog]").forEach((b) =>
    b.addEventListener("click", async () => {
      const ligada = b.dataset.state === "active";
      b.disabled = true;
      try {
        await gateway(
          `github/repos/${repo}/actions/workflows/${b.dataset.tog}/${ligada ? "disable" : "enable"}`,
          { method: "PUT" },
        );
        toast(ligada ? "Trigger desabilitado" : "Trigger habilitado");
        void tasks(el);
      } catch (err) {
        toast((err as SourceError).message);
        b.disabled = false;
      }
    }),
  );

  el.querySelectorAll<HTMLButtonElement>("[data-del]").forEach((b) =>
    b.addEventListener("click", async () => {
      if (!confirm("Excluir o trigger apaga o arquivo do workflow. Continuar?")) return;
      b.disabled = true;
      try {
        await gateway(`github/repos/${repo}/contents/${b.dataset.path}`, {
          method: "DELETE",
          body: JSON.stringify({ message: `remove trigger ${b.dataset.path}`, sha: b.dataset.sha }),
        });
        toast("Trigger removido");
        void tasks(el);
      } catch (err) {
        toast((err as SourceError).message);
        b.disabled = false;
      }
    }),
  );
}

// ----------------------------------------------------------- trigger detail

async function detalheTrigger(el: HTMLElement, id: string): Promise<void> {
  injetaCss();
  const header = cabecalho("triggers");
  let ctx: { repo: string; path: string; sha: string } | null = null;

  await load(
    el,
    header,
    async () => {
      const repo = await repoAtual();
      const wf = await gateway<Wf>(`github/repos/${repo}/actions/workflows/${id}`);
      const f = await gateway<{ content: string; sha: string }>(
        `github/repos/${repo}/contents/${wf.path}`,
      );
      const slug = wf.path.split("/").pop()!.replace(/\.ya?ml$/, "");
      // Cada fire é uma task: issue etiquetada trigger:<slug> (contrato 03).
      const fires = (
        await gateway<GhIssue[]>(
          `github/repos/${repo}/issues?labels=trigger:${encodeURIComponent(slug)}&state=all&sort=created&direction=desc&per_page=50`,
        ).catch(() => [] as GhIssue[])
      ).filter((i) => !i.pull_request);
      ctx = { repo, path: wf.path, sha: f.sha };

      return `
        <a class="btn tasks-voltar" href="#/tasks/triggers">← Triggers</a>
        <div class="card tasks-bloco">
          <div class="card-head">
            <span class="card-title">${esc(wf.name)}</span>
            ${wf.state === "active" ? pill("Enabled", "ok") : pill("Disabled", "idle")}
          </div>
          <div class="card-body">
            <div class="field">
              <label class="label" for="tgy">Configuração (workflow YAML)</label>
              <textarea class="textarea tasks-yaml" id="tgy">${esc(b64d(f.content))}</textarea>
              <span class="hint">Salvar commita ${esc(wf.path)} — toda config do trigger, prompt incluso, vive neste arquivo.</span>
            </div>
            <div class="tasks-right"><button class="btn btn-primary" id="tasks-salvar" type="button">Save changes</button></div>
          </div>
        </div>
        <div class="card tasks-bloco">
          <div class="card-head"><span class="card-title">Recent fires</span></div>
          <div class="card-body">
            ${
              fires
                .map(
                  (i) => `
              <div class="tasks-fire">
                <a href="#/tasks/${i.number}">${esc(i.title)}</a>
                <span>${i.state === "closed" ? pill("done", "ok") : pill("running", "run")} · ${esc(new Date(i.created_at).toLocaleString("pt-BR"))}</span>
              </div>`,
                )
                .join("") ||
              `<p class="hint">Nenhum disparo ainda — cada fire é uma issue com a label trigger:${esc(slug)}.</p>`
            }
          </div>
        </div>`;
    },
    () => {
      const botao = el.querySelector<HTMLButtonElement>("#tasks-salvar");
      botao?.addEventListener("click", async () => {
        if (!ctx) return;
        const texto = el.querySelector<HTMLTextAreaElement>("#tgy")!.value;
        botao.disabled = true;
        try {
          await gateway(`github/repos/${ctx.repo}/contents/${ctx.path}`, {
            method: "PUT",
            body: JSON.stringify({
              message: `trigger: edição de ${ctx.path}`,
              content: b64e(texto),
              sha: ctx.sha,
            }),
          });
          toast("Alterações commitadas");
          void tasks(el);
        } catch (e) {
          toast((e as SourceError).message);
          botao.disabled = false;
        }
      });
    },
  );
}

// ----------------------------------------------------------------- archived

// Fora do board ficam os status de tipo `canceled` do time (Canceled e
// Duplicate) — é o que o Linear tem de "saiu do fluxo". Sem label inventada.
async function paginaArchived(el: HTMLElement, header: string): Promise<void> {
  await load(el, header, async () => {
    const [statuses, issues] = await Promise.all([linear.statuses(), linear.issues()]);
    const fora = new Set(statuses.filter((s) => s.type === "canceled").map((s) => s.name));
    const dela = issues.filter((i) => i.status && fora.has(i.status));

    if (!dela.length) {
      return empty(
        "Nada arquivado",
        `Task em ${[...fora].join(" ou ") || "status cancelado"} no Linear aparece aqui.`,
      );
    }
    return table(
      ["Title", "ID", "Status", "Updated"],
      dela.map(
        (i) => `
        <tr>
          <td>${i.url ? `<a href="${esc(i.url)}" target="_blank" rel="noopener">${esc(i.title)}</a>` : esc(i.title)}</td>
          <td class="mono">${esc(i.id)}</td>
          <td>${esc(i.status ?? "—")}</td>
          <td>${esc(since(i.updatedAt ?? null))}</td>
        </tr>`,
      ),
    );
  });
}

// ------------------------------------------------------------------ checagem

// humanCron é o parser do arquivo e colunasDe decide o board — as duas menores
// checagens que quebram se eles quebrarem.
if (import.meta.env.DEV) {
  console.assert(humanCron("0 1 * * 1") === "At 01:00 AM, only on Monday", "humanCron: semanal");
  console.assert(humanCron("* * * * *") === "Every minute", "humanCron: minuto");
  console.assert(humanCron("*/5 * * * *") === "Every 5 minutes", "humanCron: passo");
  console.assert(humanCron("lixo") === "lixo", "humanCron: fallback");

  // Os oito status reais do time bymav, na ordem em que a fonte devolve.
  const st = (name: string, type: string): LinearStatus => ({ id: name, name, type });
  const colunas = colunasDe([
    st("Triage", "triage"),
    st("Backlog", "backlog"),
    st("Todo", "unstarted"),
    st("In Progress", "started"),
    st("In Review", "started"),
    st("Done", "completed"),
    st("Canceled", "canceled"),
    st("Duplicate", "canceled"),
  ]).map((c) => c.name);
  console.assert(
    colunas.join(" · ") === "Backlog · Todo · In Progress · In Review · Done",
    "colunasDe: cinco colunas na ordem do fluxo, sem triage nem canceled",
  );
}
